import type { CSSObject } from '@mui/material/styles';

// ----------------------------------------------------------------------

/**
 * Creates a CSS object for layered background gradients or images.
 *
 * @param images - Gradient strings or image URLs.
 * @param sizes - (Optional) Background sizes for each layer. Defaults to 'cover'.
 * @param positions - (Optional) Background positions for each layer. Defaults to 'center'.
 * @param repeats - (Optional) Background repeat settings for each layer. Defaults to 'no-repeat'.
 * @returns A CSS object with styles.
 *
 * @example
 * // With gradient and image overlay
 * ...theme.mixins.bgGradient({
 *   images: [
 *     `linear-gradient(0deg, ${varAlpha(theme.vars.palette.primary.darkerChannel, 0.8)}, ${varAlpha(theme.vars.palette.primary.darkerChannel, 0.8)})`,
 *     `url(/assets/overlay.png)`,
 *   ],
 *   sizes: ['cover', '80px 80px'],
 *   positions: ['center', 'top right'],
 *   repeats: ['no-repeat', 'repeat']
 * })
 *
 * @example
 * // With a single gradient only
 * ...theme.mixins.bgGradient({
 *   images: [
 *     `linear-gradient(0deg, ${varAlpha(theme.vars.palette.primary.darkerChannel, 0.8)}, ${varAlpha(theme.vars.palette.primary.darkerChannel, 0.8)})`,
 *   ],
 * })
 */

// ----------------------------------------------------------------------

/**
 * Procedural Perlin noise texture generated via SVG <feTurbulence>.
 * Gives gradients a tactile, high-end grain texture and eliminates color banding.
 */
export const NOISE_SVG_DATA_URI =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.80' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E";

/**
 * Returns CSS properties for an overlay pseudo-element (::before or ::after)
 * applying the SVG feTurbulence grain texture.
 */
export const grainOverlay = (opacity = 0.15): CSSObject => ({
  content: '""',
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  opacity,
  backgroundImage: `url("${NOISE_SVG_DATA_URI}")`,
  mixBlendMode: 'overlay',
  pointerEvents: 'none',
  borderRadius: 'inherit',
  zIndex: 0,
});

/**
 * Generates styles for a gradient background layered with an SVG feTurbulence grain texture.
 */
export type GrainyGradientOptions = {
  gradient: string;
  opacity?: number;
  hoverGradient?: string;
};

export function grainyGradient({
  gradient,
  opacity = 0.15,
  hoverGradient,
}: GrainyGradientOptions): CSSObject {
  return {
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: gradient,
    '&::before': grainOverlay(opacity),
    ...(hoverGradient && {
      '&:hover': {
        backgroundImage: hoverGradient,
      },
    }),
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  };
}

export function grainyGradientSx(
  gradient: string,
  opacity = 0.15,
  hoverGradient?: string
): CSSObject {
  return grainyGradient({ gradient, opacity, hoverGradient });
}

export type BgGradientProps = {
  images: string[];
  sizes?: string[];
  positions?: string[];
  repeats?: string[];
  grainy?: boolean;
  noiseOpacity?: number;
};

export function bgGradient({
  sizes,
  repeats,
  images,
  positions,
  grainy,
  noiseOpacity = 0.15,
}: BgGradientProps): CSSObject {
  const hasGradient = images?.some((img) => img.includes('gradient'));
  const applyNoise = grainy ?? hasGradient;

  const base: CSSObject = {
    backgroundImage: images?.join(', '),
    backgroundSize: sizes?.join(', ') ?? 'cover',
    backgroundRepeat: repeats?.join(', ') ?? 'no-repeat',
    backgroundPosition: positions?.join(', ') ?? 'center',
  };

  if (applyNoise) {
    return {
      ...base,
      position: 'relative',
      '&::before': grainOverlay(noiseOpacity),
      '& > *': {
        position: 'relative',
        zIndex: 1,
      },
    };
  }

  return base;
}

// ----------------------------------------------------------------------

/**
 * Creates a CSS object for a blurred background effect with optional image overlay.
 *
 * @param color - Background color with optional transparency.
 * @param blur - (Optional) Blur intensity in pixels. Defaults to 6.
 * @param imgUrl - (Optional) Background image URL to apply the blur effect on.
 * @returns A CSS object with styles.
 *
 * @example
 * // With image overlay
 * ...theme.mixins.bgBlur({
 *   color: varAlpha(theme.vars.palette.background.paperChannel, 0.8),
 *   imgUrl: '/assets/overlay.png',
 *   blur: 8,
 * })
 *
 * @example
 * // With color only
 * ...theme.mixins.bgBlur({
 *   color: varAlpha(theme.vars.palette.background.paperChannel, 0.8),
 * })
 */

export type BgBlurProps = {
  color: string;
  blur?: number;
  imgUrl?: string;
};

export function bgBlur({ color, blur = 6, imgUrl }: BgBlurProps): CSSObject {
  if (imgUrl) {
    return {
      position: 'relative',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundImage: `url(${imgUrl})`,
      '&::before': {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 9,
        content: '""',
        width: '100%',
        height: '100%',
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        backgroundColor: color,
      },
    };
  }
  return {
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    backgroundColor: color,
  };
}
